import { ChangeDetectionStrategy, Component, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CUSTOM_DATE_FORMATS } from '@nagel-utils';
import { CustomDateAdapter } from '../single-date-time-picker/custom-date-adapter';
import { TimePickerComponent } from '../time-picker/time-picker.component';
import { HOURS_KEY, INVALID_TIME_ERROR, MINUTES_KEY } from '@nagel-consts';
import { SingleDateTimeService } from '@nagel-services';
import { TimeRangeValidators } from '@nagel-validators';

@Component({
    selector: 'date-time-picker',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        FormsModule,
        MatDatepickerModule,
        TimePickerComponent
    ],
    providers: [
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: CUSTOM_DATE_FORMATS
        },
    ],
    templateUrl: './date-time-picker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateTimePickerComponent {
  @Output() dateTimeSelected = new EventEmitter<Date>();
  @ViewChild('picker') picker!: MatDatepicker<Date>;

  timeLabel = $localize`Time `;

  dateTimeForm = new FormGroup({
      date: new FormControl<Date | null>(null),
      time: new FormGroup({
          [HOURS_KEY]: new FormControl('00'),
          [MINUTES_KEY]: new FormControl('00')
      }, { validators: [TimeRangeValidators.isTimeRangeValidValidator(HOURS_KEY, MINUTES_KEY)] })
  });

  constructor(public singleDateTimeService: SingleDateTimeService) {
  }

  ngOnInit() {
      this.singleDateTimeService.setForm(this.dateTimeForm);
  }

  onSubmit(event: Event) {
      event.preventDefault();

      this.setDefaultDateIfEmpty();

      if (this.dateTimeForm.valid) {
          this.updateDateWithTime();

          const selectedDateTime = this.singleDateTimeService.getDateFormControl().value;
          this.dateTimeSelected.emit(selectedDateTime!);

          this.picker.close();
      }
  }

  private setDefaultDateIfEmpty() {
      if (this.dateTimeForm.value.date === null) {
          this.singleDateTimeService.setDate(new Date());
      }
  }

  private updateDateWithTime() {
      const currentDate = this.singleDateTimeService.getDateFormControl().value;

      const updatedDate = new Date(currentDate!);
      const hours = this.getHours();
      const minutes = this.getMinutes();

      updatedDate.setHours(hours, minutes, 0, 0);
      this.singleDateTimeService.setDate(updatedDate);
  }

  private getHours() {
      return parseInt(this.singleDateTimeService.getHoursControl().value || '0', 10);
  }

  private getMinutes() {
      return parseInt(this.singleDateTimeService.getMinutesControl().value || '0', 10);
  }

  onCancel(event: Event) {
      event.preventDefault();
      this.singleDateTimeService.resetForm();
      this.dateTimeSelected.emit(undefined);
      this.picker.close();
  }

  getErrorMessage = () => {
      let message = '';

      if (this.singleDateTimeService.getTimeFormGroup().hasError(INVALID_TIME_ERROR)) {
          message = $localize`Invalid time.`;
      }

      return message;
  };

  shouldDisableApplyButton = () => {
      return this.singleDateTimeService.getTimeFormGroup().hasError(INVALID_TIME_ERROR);
  };

}